import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useForm } from "react-hook-form";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import Button from "@/components/Button";
import EmptyState from "@/components/EmptyState";
import Tabs from "@/components/Tabs";
import { CardSkeleton } from "@/components/Skeleton";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanManageCareerPlanBool,
  useCanViewCareerPlanBool,
  useRequireCapability
} from "@/hooks/useCapabilities";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { Path } from "@/router";
import { GET_CAREER_PLAN_QUERY } from "../queries";
import { UPSERT_CAREER_INTENT_MUTATION } from "../mutations";
import CareerGoalsForm from "./_CareerGoalsForm";
import CareerGoalsTab from "./_CareerGoalsTab";
import ExperienceIndexAnswersTab from "./_ExperienceIndexAnswersTab";
import ExperienceIndexResultsTab from "./_ExperienceIndexResultsTab";
import {
  buildUpsertCareerIntentInput,
  defaultCareerIntentFormValues,
  getCareerIntentSteps,
  hydrateCareerIntentForm,
  validateCareerIntentStep,
  type CareerIntentFormValues
} from "./careerIntentForm";
import { buildCareerPlanHistoryRows, getCurrentCareerIntent, goalsAsOfDay, LATEST_DAY_KEY } from "./careerPlanHistory";
import { getExperienceIndexCompletion, getExperienceIndexHubState } from "./careerPlanHubSummaries";
import {
  careerPlanCompetencyIdFromSearch,
  careerPlanLatestSearchParams,
  careerPlanLatestTabFromSearch,
  type CareerPlanLatestTab
} from "./careerPlanLatest";

export default function CareerPlanDayPage() {
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewCareerPlan });
  const { pgaId, day } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const canViewCareerPlan = useCanViewCareerPlanBool();
  const canManageCareerPlan = useCanManageCareerPlanBool();
  const careerTargetsEnabled = flags["pga-career-targets"] === true;
  const dayKey = day || LATEST_DAY_KEY;
  const isLatest = dayKey === LATEST_DAY_KEY;
  const canEditCurrent = canManageCareerPlan && isLatest;
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepError, setStepError] = useState<string>();

  const { data, loading, error, refetch } = useQuery(GET_CAREER_PLAN_QUERY, {
    variables: { pgaId: pgaId || "" },
    skip: !pgaId || !flagsLoaded || !capabilitiesLoaded || !careerTargetsEnabled || !canViewCareerPlan
  });

  const careerIntents = useMemo(
    () => (data?.person?.careerIntents ?? []).filter((goal) => Boolean(goal?.id && goal?.createdAt)),
    [data?.person?.careerIntents]
  );
  const assessment = data?.person?.experienceIndexAssessment ?? null;
  const historyRows = useMemo(
    () =>
      buildCareerPlanHistoryRows({
        goals: careerIntents,
        assessment,
        includeExperienceIndexHistory: false
      }),
    [assessment, careerIntents]
  );
  const row = historyRows.find((item) => item.dayKey === dayKey);
  const goals = useMemo(() => goalsAsOfDay(careerIntents, dayKey), [careerIntents, dayKey]);
  const currentGoal = isLatest ? getCurrentCareerIntent(data?.person?.careerIntent, careerIntents) : (goals[0] ?? null);

  const form = useForm<CareerIntentFormValues>({
    defaultValues: defaultCareerIntentFormValues()
  });
  const formValues = form.watch();
  const steps = useMemo(
    () => getCareerIntentSteps({ selectedIntents: formValues.selectedIntents }),
    [formValues.selectedIntents]
  );
  const currentStep = steps[Math.min(stepIndex, Math.max(steps.length - 1, 0))];
  const isFirstStep = stepIndex === 0;
  const isLastStep = Boolean(currentStep) && stepIndex === steps.length - 1;
  const showProgress = formValues.selectedIntents.length > 0;

  useEffect(() => {
    if (stepIndex >= steps.length) {
      setStepIndex(Math.max(steps.length - 1, 0));
    }
  }, [stepIndex, steps.length]);

  const [upsertCareerIntent] = useMutation(UPSERT_CAREER_INTENT_MUTATION);

  const activeTab: CareerPlanLatestTab = isEditing || !isLatest ? "goals" : careerPlanLatestTabFromSearch(searchParams);
  const focusedCompetencyId =
    isLatest && activeTab === "answers" ? careerPlanCompetencyIdFromSearch(searchParams) : null;
  const setActiveTab = (tab: CareerPlanLatestTab) => {
    if (isEditing) return;
    setSearchParams(careerPlanLatestSearchParams(tab));
  };
  const openCompetencyAnswers = (competencyId: string) => {
    if (isEditing) return;
    setSearchParams(careerPlanLatestSearchParams("answers", competencyId));
  };
  const clearFocusedCompetency = () => {
    if (isEditing) return;
    setSearchParams(careerPlanLatestSearchParams("answers"));
  };

  const startEditing = () => {
    form.reset(hydrateCareerIntentForm(currentGoal));
    setStepIndex(0);
    setStepError(undefined);
    setIsEditing(true);
    setSearchParams({});
  };

  const cancelEditing = () => {
    form.reset(hydrateCareerIntentForm(currentGoal));
    setStepIndex(0);
    setStepError(undefined);
    setIsEditing(false);
  };

  const saveGoals = form.handleSubmit(async (values) => {
    if (!pgaId) return;

    setIsSubmitting(true);
    try {
      const { data: mutationData, errors } = await upsertCareerIntent({
        variables: { input: buildUpsertCareerIntentInput(pgaId, values) }
      });

      if (errors?.length) {
        showErrorSnackbar(errors[0]?.message || "Failed to save Career Goals");
        return;
      }

      const payload = mutationData?.upsertCareerIntent;
      if (!payload?.success) {
        showErrorSnackbar(payload?.message || "Failed to save Career Goals");
        return;
      }

      showSnackbar("Career Goals saved");
      setIsEditing(false);
      setStepIndex(0);
      setStepError(undefined);
      void refetch();
    } catch (saveError) {
      showErrorSnackbar(saveError instanceof Error ? saveError.message : "Failed to save Career Goals");
    } finally {
      setIsSubmitting(false);
    }
  });

  const goPrevious = () => {
    if (isSubmitting || stepIndex === 0) return;
    setStepError(undefined);
    setStepIndex((index) => index - 1);
  };

  const goContinue = () => {
    if (isSubmitting || !currentStep) return;

    const validationMessage = validateCareerIntentStep(form.getValues(), currentStep.id);
    if (validationMessage) {
      setStepError(validationMessage);
      return;
    }

    setStepError(undefined);
    if (!isLastStep) {
      setStepIndex((index) => index + 1);
      return;
    }

    void saveGoals();
  };

  if (!flagsLoaded || !capabilitiesLoaded) return null;
  if (!careerTargetsEnabled && pgaId) {
    return <Navigate to={`/people/${pgaId}/career/employment`} replace />;
  }

  const backLink = {
    label: "Back",
    to: "/people/:pgaId/career/career-profile" as Path,
    params: { pgaId: pgaId }
  } as any;

  const tabItems = [
    {
      label: "Career Goals",
      active: activeTab === "goals",
      onClick: () => setActiveTab("goals")
    },
    ...(isLatest
      ? [
          {
            label: "Experience Index Results",
            active: activeTab === "results",
            onClick: () => setActiveTab("results")
          },
          {
            label: "Experience Index Answers",
            active: activeTab === "answers",
            onClick: () => setActiveTab("answers")
          }
        ]
      : [])
  ];

  const resultScores = isLatest ? (assessment?.scores ?? []) : [];
  const roleRubrics = data?.careerRoleRubrics ?? [];
  const assessmentComplete = getExperienceIndexHubState(getExperienceIndexCompletion(assessment)) === "finished";
  const memberClass = data?.person?.latestProgram?.band?.classification?.name ?? null;
  const showCreate = canEditCurrent && !isEditing && goals.length === 0;
  const showEdit = canEditCurrent && !isEditing && goals.length > 0;

  const actions = isEditing ? (
    <>
      <Button variant="outlined" disabled={isSubmitting} onClick={cancelEditing} testId="career-goals-cancel">
        Cancel
      </Button>
      {!isFirstStep ? (
        <Button variant="outlined" disabled={isSubmitting} onClick={goPrevious} testId="career-goals-previous">
          Previous
        </Button>
      ) : null}
      <Button
        disabled={isSubmitting}
        icon={isSubmitting ? "progress_activity" : undefined}
        onClick={goContinue}
        testId={isLastStep ? "career-goals-save" : "career-goals-continue"}
      >
        {isLastStep ? "Save Plan" : "Continue"}
      </Button>
    </>
  ) : showCreate ? (
    <Button onClick={startEditing} testId="career-goals-create">
      Create Career Goals
    </Button>
  ) : showEdit ? (
    <Button variant="outlined" onClick={startEditing} testId="career-goals-edit">
      Edit
    </Button>
  ) : null;

  return (
    <FocusedPageLayout backLink={backLink} actions={actions} stickyActions>
      {pgaId ? <PersonObjectHeading pgaId={pgaId} hideActions disableSticky /> : null}

      <div data-testid="career-plan-day-page">
        <Tabs tabItems={tabItems} showWhenSingle />

        {loading ? (
          <CardSkeleton className="w-full" rows={6} />
        ) : error ? (
          <div>Error: {error.message}</div>
        ) : !isLatest && !row ? (
          <EmptyState
            title="Snapshot Not Found"
            iconName="search_off"
            caption="No Career Plan changes were recorded for this day."
          />
        ) : activeTab === "goals" ? (
          isEditing && currentStep ? (
            <CareerGoalsForm
              currentRole={memberClass}
              disabled={isSubmitting}
              errorMessage={stepError}
              form={form}
              showProgress={showProgress}
              step={currentStep.id}
              stepIndex={stepIndex}
              stepTitle={currentStep.title}
              stepsLength={steps.length}
            />
          ) : (
            <CareerGoalsTab goals={goals} isLatest={isLatest} onCreate={showCreate ? startEditing : undefined} />
          )
        ) : activeTab === "results" ? (
          <ExperienceIndexResultsTab
            assessmentComplete={assessmentComplete}
            scores={resultScores}
            rubrics={roleRubrics}
            onSelectCompetency={openCompetencyAnswers}
          />
        ) : (
          <ExperienceIndexAnswersTab
            answers={assessment?.answers ?? []}
            focusedCompetencyId={focusedCompetencyId}
            onClearFocusedCompetency={clearFocusedCompetency}
          />
        )}
      </div>
    </FocusedPageLayout>
  );
}
